import axios from "axios";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";

const API_URL = process.env.REACT_APP_API_URL || process.env.REACT_APP_LOCAL_API_URL;
const authHeaders = () => ({ "Content-Type": "application/json", token: localStorage.getItem("token") });

function MyWorkouts({ userRole }) {
  const [sessions, setSessions] = useState([]);
  const [showArchived, setShowArchived] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [busyId, setBusyId] = useState("");
  const navigate = useNavigate();

  const fetchSessions = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/training/user-sessions`, {
        headers: authHeaders(), params: { archived: showArchived ? "true" : "false" }
      });
      setSessions(response.data || []);
      setErrorMessage("");
    } catch { setErrorMessage("Kunde inte hämta dina pass"); }
  }, [showArchived]);

  useEffect(() => { fetchSessions(); }, [fetchSessions]);

  const sortedSessions = useMemo(() => [...sessions].sort((left, right) => {
    if (left.isTemplate !== right.isTemplate) return left.isTemplate ? -1 : 1;
    const leftDate = new Date(left.lastUsedAt || left.updatedAt || 0);
    const rightDate = new Date(right.lastUsedAt || right.updatedAt || 0);
    return rightDate - leftDate;
  }), [sessions]);

  const action = async (sessionId, request, success) => {
    try {
      setBusyId(sessionId);
      const response = await request();
      setStatusMessage(response.data.message || success);
      setErrorMessage("");
      await fetchSessions();
    } catch (error) {
      setErrorMessage(error.response?.data?.message || "Åtgärden misslyckades");
    } finally { setBusyId(""); }
  };

  function openSession(sessionId) { navigate("/managesession", { state: { session: sessionId } }); }
  function runSession(sessionId) { navigate("/run-session", { state: { session: sessionId } }); }

  return <div className="mx-auto w-full max-w-4xl px-4 py-4"><div className="theme-card">
    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-sky-700">Pass</p>
    <h1 className="mt-2 text-lg font-bold text-slate-800">Mina träningspass</h1>
    <p className="mt-2 text-sm text-slate-600">Mallpass visas först, därefter dina senast använda pass. Pågående skapande autosparas lokalt tills passet sparas.</p>
    <div className="mt-4 flex flex-wrap gap-2">
      <button className={`theme-badge-button ${!showArchived ? "border-sky-500 text-sky-700" : "border-slate-200"}`} onClick={() => setShowArchived(false)}>Aktiva</button>
      <button className={`theme-badge-button ${showArchived ? "border-sky-500 text-sky-700" : "border-slate-200"}`} onClick={() => setShowArchived(true)}>Arkiverade</button>
    </div>
    {errorMessage && <div className="theme-alert-error mt-4">{errorMessage}</div>}
    {statusMessage && <div className="theme-alert-success mt-4">{statusMessage}</div>}

    <div className="mt-5 space-y-4">
      {!sortedSessions.length && <div className="theme-empty-state">{showArchived ? "Du har inga arkiverade pass." : "Du har inga aktiva pass ännu."}</div>}
      {sortedSessions.map((session) => {
        const canManage = Boolean(session.canManage);
        return <article key={session._id} className="theme-panel">
          <button className="w-full text-left" onClick={() => canManage ? openSession(session._id) : runSession(session._id)} disabled={showArchived}>
            <div className="flex flex-wrap items-center gap-2">
              <h2 className="font-semibold text-slate-800">{session.title}</h2>
              {session.isTemplate && <span className="rounded-full bg-sky-100 px-3 py-1 text-xs font-semibold text-sky-700">Mall</span>}
              {session.isGroupShared && <span className="rounded-full bg-emerald-50 px-3 py-1 text-xs font-semibold text-emerald-700">Delat via grupp</span>}
            </div>
            <p className="mt-2 text-sm text-slate-600">{session.description || "Ingen beskrivning"}</p>
            <p className="mt-2 text-xs text-slate-500">{session.station?.length || 0} stationer · Senast använd: {session.lastUsedAt ? new Date(session.lastUsedAt).toLocaleString("sv-SE") : "inte ännu"}</p>
          </button>
          <div className="mt-4 flex flex-wrap gap-2">
            {showArchived ? (
              canManage && <button disabled={busyId === session._id} className="theme-badge-button border-emerald-200 text-emerald-700" onClick={() => action(session._id, () => axios.patch(`${API_URL}/training/archive-session`, { sessionId: session._id, archived: false }, { headers: authHeaders() }), "Passet återställdes")}>Återställ</button>
            ) : <>
              <button className="theme-badge-button border-emerald-200 text-emerald-700" onClick={() => runSession(session._id)}>Kör pass</button>
              <button className="theme-badge-button border-sky-200 text-sky-700" onClick={() => canManage ? openSession(session._id) : runSession(session._id)}>{canManage ? "Hantera" : "Visa"}</button>
              <button className="theme-badge-button border-sky-200 text-sky-700" onClick={() => navigate("/createsession", { state: { templateSessionId: session._id } })}>Använd som mall</button>
              <button disabled={busyId === session._id} className="theme-badge-button border-slate-200 text-slate-700" onClick={() => action(session._id, () => axios.post(`${API_URL}/training/duplicate-session-by-id`, { sessionId: session._id }, { headers: authHeaders() }), "Passet duplicerades")}>Duplicera</button>
              {canManage && userRole !== 3 && <button disabled={busyId === session._id} className="theme-badge-button border-violet-200 text-violet-700" onClick={() => action(session._id, () => axios.patch(`${API_URL}/training/template-session`, { sessionId: session._id, isTemplate: !session.isTemplate }, { headers: authHeaders() }), "Mallmarkeringen uppdaterades")}>{session.isTemplate ? "Ta bort mallmarkering" : "Markera som mall"}</button>}
              {canManage && <button disabled={busyId === session._id} className="theme-badge-button border-amber-200 text-amber-700" onClick={() => action(session._id, () => axios.patch(`${API_URL}/training/archive-session`, { sessionId: session._id, archived: true }, { headers: authHeaders() }), "Passet arkiverades")}>Arkivera</button>}
            </>}
          </div>
        </article>;
      })}
    </div>
    {!showArchived && <Link to="/createsession"><button className="theme-button-primary mt-5">Skapa nytt pass</button></Link>}
  </div></div>;
}

export default MyWorkouts;
